import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import AttachFileIcon   from '@mui/icons-material/AttachFile';
import ImageIcon        from '@mui/icons-material/Image';
import SettingsIcon     from '@mui/icons-material/Settings';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';

const FactCheckInput = ({
  claim,
  setClaim,
  handleDetectMode,          // submit handler from parent
  handleDrop,
  handleDragOver,
  loading,
  darkMode,

  // hidden file inputs (optional – you can also let this component manage its own)
  attachmentInputRef,
  imageInputRef,

  // parent can hide results/reset analysis on typing
  setShowResults,
  setAdvancedAnalysis,

  allowedDocTypes = [],
  onToolSelect,

  // NEW preferred props (controlled AI toggle)
  aiMode,                    // boolean | undefined
  onModeChange,              // (checked:boolean)=>void

  // Optional: make the arrow a “Back to top” button
  arrowAsBackToTop = false,
  onBackToTop,               // () => void

  // Indicators & callbacks for file selection
  onAttachmentSelected,      // (file)=>void
  onImageSelected,           // (file)=>void

  // Typing indicator
  typingLong = false,

  // LEGACY (kept for compatibility)
  processMode = 'claim',
  setProcessMode = () => {},
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [toolsAnchorEl, setToolsAnchorEl] = useState(null);
  const openTools = Boolean(toolsAnchorEl);

  const [docChip, setDocChip] = useState(null);   // {name:string} | null
  const [imgChip, setImgChip] = useState(null);   // {name:string} | null

  // Prefer new controlled prop
  const isAi = typeof aiMode === 'boolean' ? aiMode : (processMode === 'ai');
  const setIsAi = useCallback((checked) => {
    onModeChange?.(checked);
    setProcessMode?.(checked ? 'ai' : 'claim');
  }, [onModeChange, setProcessMode]);

  const handleToolsClick = (e) => {
    e.stopPropagation();
    setToolsAnchorEl(e.currentTarget);
  };
  const handleToolsClose = () => setToolsAnchorEl(null);
  const handleTool = (toolId) => {
    onToolSelect?.(toolId);
    handleToolsClose();
  };

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setShowResults?.(false);
    setAdvancedAnalysis?.(null);
    setClaim(v);
  }, [setClaim, setShowResults, setAdvancedAnalysis]);

  const submit = useCallback(() => {
    if (arrowAsBackToTop) {
      onBackToTop?.();
      return;
    }
    if (!loading && claim && claim.trim()) {
      handleDetectMode?.();
    }
  }, [arrowAsBackToTop, onBackToTop, loading, claim, handleDetectMode]);

  // Toolbar sizing (inside the field, bottom-right)
  const TOOLBAR_HEIGHT = isMobile ? 26 : 22;  // ↓ reduced by 4px
  const TOOLBAR_GAP = isMobile ? 0.5 : 1;
  // Reserve enough padding so text doesn’t overlap the toolbar
  const RIGHT_PADDING = isMobile ? 108 : 160;

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      sx={{ width: '100%', maxWidth: 900, mx: 'auto', pb: 0 }}
    >
      <Box sx={{ position: 'relative' }}>
        <TextField
          multiline
          fullWidth
          minRows={isMobile ? 3 : 2}
          maxRows={6}
          placeholder="Enter your claim or question…"
          value={claim}
          disabled={loading}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{
            borderRadius: 3,
            '& .MuiOutlinedInput-root': {
              p: 1.2,
              lineHeight: 1.4,
              borderRadius: 3,
              backgroundColor: (t) => alpha(t.palette.background.paper, darkMode ? 0.18 : 0.55),
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              transition: 'box-shadow .2s ease, border-color .2s ease, background-color .2s ease',
              boxShadow: darkMode ? '0 8px 24px rgba(0,0,0,.35)' : '0 8px 24px rgba(0,0,0,.12)',
              '& fieldset': {
                borderWidth: 1,
                borderColor: (t) =>
                  alpha(darkMode ? t.palette.common.white : t.palette.common.black, darkMode ? 0.18 : 0.12),
              },
              '&:hover fieldset': { borderColor: (t) => alpha(t.palette.primary.main, 0.5) },
              '&.Mui-focused fieldset': { borderWidth: 1.5, borderColor: (t) => t.palette.primary.main },
              '&.Mui-disabled': {
                backgroundColor: (t) => alpha(t.palette.background.paper, darkMode ? 0.12 : 0.4),
                boxShadow: 'none',
              },
            },
            // Give the textarea room for the overlaid toolbar
            '& .MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline': {
              pr: `${RIGHT_PADDING}px`,
              pb: `${TOOLBAR_HEIGHT + (isMobile ? 12 : 8)}px`,
            },
            '& .MuiInputBase-input::placeholder': { opacity: 0.8 },
          }}
        />

        {/* INSIDE TOOLBAR (overlaid in the bottom-right corner) */}
        <Box
          sx={{
            position: 'absolute',
            right: isMobile ? 6 : 10,
            bottom: isMobile ? 6 : 8,
            height: TOOLBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: TOOLBAR_GAP,
            zIndex: 1,
            '& .MuiIconButton-root': {
              color: (t) => (t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.main),
            },
          }}
        >
          {/* AI toggle — text label hidden on phones */}
          {isMobile ? (
            <Tooltip title="AI mode">
              <Switch
                size="small"
                checked={!!isAi}
                onChange={(e) => setIsAi(!!e.target.checked)}
                inputProps={{ 'aria-label': 'AI mode' }}
              />
            </Tooltip>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!isAi}
                  onChange={(e) => setIsAi(!!e.target.checked)}
                  inputProps={{ 'aria-label': 'AI mode' }}
                />
              }
              label="AI"
              sx={{ mr: 0, color: 'text.secondary', userSelect: 'none' }}
              componentsProps={{ typography: { variant: 'caption' } }}
            />
          )}

          {typingLong && !loading && <CircularProgress size={20} thickness={5} />}

          {/* Submit / Back to top */}
          <Tooltip title={arrowAsBackToTop ? 'Back to top' : 'Submit'}>
            <span>
              <IconButton
                onClick={submit}
                disabled={!arrowAsBackToTop && (loading || !claim || !claim.trim())}
                sx={{ p: isMobile ? 0.75 : 0.5 }}
                aria-label={arrowAsBackToTop ? 'back-to-top' : 'submit-claim'}
              >
                <ArrowCircleUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Tools */}
          <Tooltip title="Tools">
            <IconButton onClick={handleToolsClick} sx={{ p: isMobile ? 0.75 : 0.5 }}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Image attach */}
          <Tooltip title={imgChip ? `Saved: ${imgChip.name}` : 'Attach image'}>
            <IconButton component="label" sx={{ p: isMobile ? 0.75 : 0.5 }}>
              <ImageIcon fontSize="small" />
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onImageSelected?.(file);
                    setImgChip({ name: file.name });
                  }
                  e.target.value = '';
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Doc attach */}
          <Tooltip title={docChip ? `Saved: ${docChip.name}` : 'Attach document'}>
            <IconButton component="label" sx={{ p: isMobile ? 0.75 : 0.5 }}>
              <AttachFileIcon fontSize="small" />
              <input
                ref={attachmentInputRef}
                type="file"
                accept={Array.isArray(allowedDocTypes) ? allowedDocTypes.join(',') : ''}
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onAttachmentSelected?.(file);
                    setDocChip({ name: file.name });
                  }
                  e.target.value = '';
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Saved chips (hide on very small screens to avoid clutter) */}
        {(imgChip || docChip) && !isMobile && (
          <Box
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
            }}
          >
            {imgChip && (
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label={imgChip.name.length > 16 ? `${imgChip.name.slice(0, 14)}…` : imgChip.name}
                onDelete={() => setImgChip(null)}
              />
            )}
            {docChip && (
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label={docChip.name.length > 16 ? `${docChip.name.slice(0, 14)}…` : docChip.name}
                onDelete={() => setDocChip(null)}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Tools menu */}
      <Menu anchorEl={toolsAnchorEl} open={openTools} onClose={handleToolsClose}>
        <MenuItem onClick={() => handleTool('financialAid')}>Financial Aid Lookup</MenuItem>
        <MenuItem onClick={() => handleTool('smallBusiness')}>Small Business Support</MenuItem>
        <MenuItem onClick={() => handleTool('communityServices')}>Community Services</MenuItem>
        <MenuItem onClick={() => handleTool('jobCareerHelp')}>Job & Career Help</MenuItem>
        <MenuItem onClick={() => handleTool('legislationChecker')}>Legislation Checker</MenuItem>
      </Menu>
    </Box>
  );
};

export default FactCheckInput;

